import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Nav from "react-bootstrap/Nav";
import PDPHeaderActions from "./pdpComponents/PDPHeaderActions";
import PDPHeaderStick from "./pdpComponents/PDPHeaderStick";
import { getDHM } from "@/utils/dhmCalculation";
import { trackButtonClick, trackPageMount } from "@/utils/analytics";
import { useNavigate } from "react-router";
import { markInternalNavigation } from "@/utils/attributionCapture";

let hasInitiatedRef = false;

const PDPHeader = ({taxRecord, priceHistory, statusPayload, pdpBidCenter, listingType, status, offerType, offerCenterDetail, financialType, pdpPropertyFees, isWatchProperty, showInvestmentCal, propertyDetails, isForeclosure, hidePriceHistory, hidePropertyTaxes}) => {
  const navigate = useNavigate();

  const user_info = useSelector((state) => state?.user?.user);
  const pdpInitData = useSelector((state) => state.pdp.pdpInitData);
  const latestBidCenterData = useSelector((state) => state.pdp?.latestBidCenterData);

  const [showBidHeader, setShowBidHeader] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");
  const [getCurrentTime, setCurrentTime] = useState();
  const propCategory = pdpInitData?.propertyDetails?.commonAttributes?.propCategory;

  // CMS-based tab hiding for Local Info section
  const propertyId = String(propertyDetails?.propertyId || "");
  const isPropertyInCmsNode = (cmsNode, id) => {
    if (!cmsNode || typeof cmsNode !== 'object' || !id) return false;
    const keys = Object.keys(cmsNode);
    const values = Object.values(cmsNode).filter(v => typeof v === 'string');
    return keys.includes(id) || values.includes(id);
  };
  const shouldHidePriceHistory = isPropertyInCmsNode(hidePriceHistory, propertyId);
  const shouldHidePropertyTaxes = isPropertyInCmsNode(hidePropertyTaxes, propertyId);
  const hasDemographics = propCategory === "COMMERCIAL";
  const shouldHideLocalInfoNav = shouldHidePriceHistory && shouldHidePropertyTaxes && !hasDemographics;

  const handleShowNavbar = useCallback(() => {
  const PDElement = document.getElementById("PropertyDetails");
  const PDOffset = PDElement?.offsetTop;
  const LocElement = document.getElementById("LocalInfo");
  const LocOffset = LocElement?.offsetTop;
  const InvCalElement = document.getElementById("InvestmentCalculator");
  const IncCalOffset = InvCalElement?.offsetTop;
  const DocElement = document.getElementById("Documents");
  const DocOffset = DocElement?.offsetTop;
  if (propCategory === "COMMERCIAL") {
    if (window.scrollY >= 0 && window.scrollY < PDOffset - 70) {
      setActiveTab("Overview");
    } else if (window.scrollY >= PDOffset - 70 && window.scrollY < IncCalOffset - 70) {
      setActiveTab("PropertyDetails");
    } else if (window.scrollY >= IncCalOffset - 70 && window.scrollY < DocOffset - 70) {
      setActiveTab("InvestmentCalculator");
    } else if (window.scrollY >= DocOffset - 70 && window.scrollY < LocOffset - 70) {
      setActiveTab("Documents");
    } else if (window.scrollY >= LocOffset - 70) {
      setActiveTab("LocalInfo");
    }
  } else {
    if (window.scrollY >= 0 && window.scrollY < PDOffset - 70) {
      setActiveTab("Overview");
    } else if (window.scrollY >= PDOffset - 70 && window.scrollY < LocOffset - 70) {
      setActiveTab("PropertyDetails");
    } else if (window.scrollY >= IncCalOffset - 70 && window.scrollY < LocOffset - 70) {
      setActiveTab("InvestmentCalculator");
    } else if (window.scrollY >= LocOffset - 70 && window.scrollY < DocOffset - 70) {
      setActiveTab("LocalInfo");
    } else if (window.scrollY >= DocOffset - 70) {
      setActiveTab("Documents");
    }
  }
    const DescElement = document.getElementById("Description");
    const DescOffset = DescElement?.offsetTop - DescElement?.offsetHeight - 30;
    if (window.scrollY >= DescOffset) {
      setShowBidHeader(true);
    } else {
      setShowBidHeader(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleShowNavbar);
    return () => {
      window.removeEventListener("scroll", handleShowNavbar);
    }
  }, []);

  const scrollToSelectedTab = (tab) => {
    setActiveTab(tab);
    if (tab === "PropertyDetails") {
      const element = document.getElementById("PropertyDetails");
      window.scrollTo({
        top: element?.offsetTop - 70,
        behavior: "smooth",
      });
    } else if (tab === "Documents") {
      const docElement = document.getElementById("Documents");
      window.scrollTo({
        top: docElement?.offsetTop - 70,
        behavior: "smooth",
      });
    } else if (tab === "LocalInfo") {
      const locElement = document.getElementById("LocalInfo");
      window.scrollTo({
        top: locElement?.offsetTop - 70,
        behavior: "smooth",
      });
    } else if (tab === "InvestmentCalculator") {
      const incCalElement = document.getElementById("InvestmentCalculator");
      window.scrollTo({
        top: incCalElement?.offsetTop - 70,
        behavior: "smooth",
      });
    } else if (tab === "Overview") {
      window.scrollTo(0, 0);
    }

    trackButtonClick({
      eventName: "quicklink_click",
      eventParams: { tab },
      eventCategory: "NEW-PDP",
      eventSource: "on tab click",
      eventLabel: `${propertyDetails?.propertyId}|${
        propertyDetails?.listingId
      }|${propertyDetails?.address?.streetNumber} ${
        propertyDetails?.address?.streetName
      }, ${propertyDetails?.address?.county}, ${
        propertyDetails?.address?.state
      }, ${propertyDetails?.address?.city}, ${
        propertyDetails?.address?.zip
      }|Referrer: Other|Foreclosure Property: ${
        isForeclosure ? "Yes" : "No"
      }|Status: ${propertyDetails?.commonAttributes?.status || "N/A"}`,
    });
  };

  useEffect(() => {
    const currentDate = new Date();
    const futureDate = new Date();
    const until = latestBidCenterData?.auctionTimeLeft / 1000;
    futureDate.setDate(currentDate.getDate() + parseInt(until / (60 * 60 * 24), 10))
    futureDate.setHours(currentDate.getHours() + parseInt(until / (60 * 60), 10) % 24)
    futureDate.setMinutes(currentDate.getMinutes() + parseInt(until / 60, 10) % 60)
    futureDate.setSeconds(currentDate.getSeconds() + until % 60)
    const intervalId = setInterval(() => {
      setCurrentTime(getDHM(futureDate))
    }, 1000);
    return () => clearInterval(intervalId);
  }, [latestBidCenterData?.auctionTimeLeft]);

  useEffect(() => {
    if (showInvestmentCal !== undefined && !hasInitiatedRef) {
      trackPageMount({
        pageName: "investment_calculator_mount",
        eventCategory: "NEW-PDP",
        eventLabel: `PropertyId: ${propertyDetails?.propertyId}`,
        eventParams: { initiated: showInvestmentCal ? "yes" : "no" },
      });
      hasInitiatedRef = true;
    }
  }, [showInvestmentCal]);

  return (
    <section className="pdp-container pdpHeader">
      <div className="our-agent-single pdp-container container p0">
        <div className="pdp-container container p0">
          <div className="pdpHeaderContainer pl0">
            <div className={`pdp-header-left pl0 ml0 ${showBidHeader ? '' : 'pdp-header-border'}`}>
              <Breadcrumb style={{ paddingBottom: 8, paddingTop: 3 }}>
                <Breadcrumb.Item
                  as="nav"
                  style={{ paddingRight: 45 }}
                  // href={pdpInitData?.propertyDetails?.address?.stateLandingUrl}
                  onClick={() => {
                    let goBackSearch = localStorage.getItem("goBackSearch");
                    goBackSearch = goBackSearch && JSON.parse(goBackSearch);
                    markInternalNavigation();
                    if (goBackSearch?.searchURI) {
                      navigate(goBackSearch?.searchURI);
                    } else {
                      navigate(pdpInitData?.propertyDetails?.address?.stateLandingUrl);
                    }
                    trackButtonClick({
                      eventName: "back_to_search_click",
                      eventCategory: "NEW-PDP",
                      eventSource: "on click",
                      eventLabel: `${propertyDetails?.propertyId}|${
                        propertyDetails?.listingId
                      }|${propertyDetails?.address?.streetNumber} ${
                        propertyDetails?.address?.streetName
                      }, ${propertyDetails?.address?.county}, ${
                        propertyDetails?.address?.state
                      }, ${propertyDetails?.address?.city}, ${
                        propertyDetails?.address?.zip
                      }|Referrer: Other|Foreclosure Property: ${
                        isForeclosure ? "Yes" : "No"
                      }|Status: ${propertyDetails?.commonAttributes?.status || "N/A"}`,
                    })
                  }}
                >
                  <span className="backtosearch">{"Back to Search"}{" "}</span>
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  as="nav"
                  href={pdpInitData?.propertyDetails?.address?.stateLandingUrl}
                  style={{ color: "  #108cf4", textDecoration: "underline" }}
                  onClick={() => {
                    trackButtonClick({
                      eventName: "state_click",
                      eventParams: { state: pdpInitData?.propertyDetails?.address?.state },
                      eventCategory: "NEW-PDP",
                      eventSource: "on click",
                      eventLabel: `${propertyDetails?.propertyId}|${
                        propertyDetails?.listingId
                      }|${propertyDetails?.address?.streetNumber} ${
                        propertyDetails?.address?.streetName
                      }, ${propertyDetails?.address?.county}, ${
                        propertyDetails?.address?.state
                      }, ${propertyDetails?.address?.city}, ${
                        propertyDetails?.address?.zip
                      }|Referrer: Other|Foreclosure Property: ${
                        isForeclosure ? "Yes" : "No"
                      }|Status: ${propertyDetails?.commonAttributes?.status || "N/A"}`,
                    })
                  }}
                >
                  <span className="breadcrumbLabel">{pdpInitData?.propertyDetails?.address?.state}</span>
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  as="nav"
                  href={pdpInitData?.propertyDetails?.address?.cityLandingUrl}
                  style={{ color: "  #108cf4", textDecoration: "underline" }}
                  className="ul"
                  onClick={() => {
                    trackButtonClick({
                      eventName: "city_click",
                      eventParams: { city: pdpInitData?.propertyDetails?.address?.city },
                      eventCategory: "NEW-PDP",
                      eventSource: "on click",
                      eventLabel: `${propertyDetails?.propertyId}|${
                        propertyDetails?.listingId
                      }|${propertyDetails?.address?.streetNumber} ${
                        propertyDetails?.address?.streetName
                      }, ${propertyDetails?.address?.county}, ${
                        propertyDetails?.address?.state
                      }, ${propertyDetails?.address?.city}, ${
                        propertyDetails?.address?.zip
                      }|Referrer: Other|Foreclosure Property: ${
                        isForeclosure ? "Yes" : "No"
                      }|Status: ${propertyDetails?.commonAttributes?.status || "N/A"}`,
                    })
                  }}
                >
                  <span className="breadcrumbLabel">{pdpInitData?.propertyDetails?.address?.city}</span>
                </Breadcrumb.Item>
                <Breadcrumb.Item as="nav" className="streatText" active>
                  {pdpInitData?.propertyDetails?.address?.streetNumber +
                    " " +
                  pdpInitData?.propertyDetails?.address?.streetName}
                </Breadcrumb.Item>
              </Breadcrumb>
              <Nav
                className=""
                variant="underline"
                onSelect={(k) => scrollToSelectedTab(k)}
                activeKey={activeTab}
              >
                <Nav.Item className="navItemContainer">
                  <Nav.Link eventKey="Overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item className="navItemContainer">
                  <Nav.Link eventKey="PropertyDetails">
                      Property Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="navItemContainer">
                  <Nav.Link eventKey="Documents">Documents</Nav.Link>
                </Nav.Item>
                {showInvestmentCal && (
                  <Nav.Item className="navItemContainer">
                    <Nav.Link eventKey="InvestmentCalculator">Rental Estimate</Nav.Link>
                  </Nav.Item>
                )}
                {!shouldHideLocalInfoNav && ((taxRecord && Object.keys(taxRecord)?.length > 0) || (priceHistory && Object.keys(priceHistory)?.length > 0) || (pdpInitData?.propertyDetails?.demographicInformation?.length > 0 && pdpInitData?.propertyDetails?.commonAttributes?.propCategory === "COMMERCIAL")) &&
                  <Nav.Item className="navItemContainer">
                    <Nav.Link eventKey="LocalInfo">Property Tax</Nav.Link>
                  </Nav.Item>
                }
              </Nav>
            </div>
            {!showBidHeader ? <PDPHeaderActions isForeclosure={isForeclosure} /> : (
              <PDPHeaderStick
                statusPayload={statusPayload}
                pdpBidCenter={pdpBidCenter}
                listingType={listingType}
                status={status}
                offerType={offerType}
                offerCenterDetail={offerCenterDetail}
                isSignedUser={user_info !== null ? true : false}
                isFcOnlineFlag={pdpInitData?.fcOnlineFlag}
                pdpData={pdpInitData}
                financialType={financialType}
                pdpPropertyFees={pdpPropertyFees}
                isWatchProperty={isWatchProperty}
                getCurrentTime={getCurrentTime}
              />
            )}
            <div className={`${showBidHeader ? 'pdp-header-border' : ''}`}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PDPHeader;
